/* eslint-disable jira/filename-case */
import React, { type ComponentType, type ReactNode } from 'react';
import { render } from 'react-dom';
import { createRoot, type Root } from 'react-dom/client';
import { getTenantContext_DEPRECATED_DO_NOT_USE } from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import '@jira/platform__global-side-effects/src/index.tsx';
import { StrictMode } from '@atlassian/jira-react-strict-mode/src/StrictMode.tsx';
import { LooselyLazy } from '@atlassian/react-loosely-lazy/init';
import { createLegacyHistory, matchRoute } from '@atlassian/react-resource-router';
import { ErrorBoundaryFlag as ErrorBoundary } from '@jira/platform__error-flag-renderer/src/ErrorBoundaryFlag.tsx';
import { getLegacyRoutes } from '@jira/platform__routes/src/getLegacyRoutes.tsx';
import { JiraRouterLinkConfiguration } from '@jira/platform__routing-router-components/src/ui/router-link-config/index.tsx';
import { Router } from '@jira/platform__routing-router-components/src/ui/router/index.tsx';
import { visualRefreshResolverOverride } from '@jira/platform__services-visual-refresh-rollout/src/visualRefreshResolverOverride.tsx';
import { getSpaRouterContext } from '@jira/platform__spa/src/services/spa-router-context/index.tsx';
import { initPlatformFeatureFlags } from '@jira/platform__utils-feature-flagging';
import getMeta from '@jira/platform__utils-get-meta/src/utils/get-meta/index.tsx';
import { setMatchedRoute } from '@jira/platform__utils-router/src/common/utils/index.tsx';

const reactRoots = new WeakMap<Element, Root>();

/** Mounts `element` into `mountPoint`: uses the React 18/19 `createRoot` API when `nike_r19_render_unmount_2` is on, else the legacy render path. */
const renderToMountPoint = (element: React.ReactElement, mountPoint: Element) => {
	if (fg('nike_r19_render_unmount_2')) {
		let root = reactRoots.get(mountPoint);

		if (!root) {
			root = createRoot(mountPoint);
			reactRoots.set(mountPoint, root);
		}

		root.render(element);
	} else {
		render(element, mountPoint);
	}
};

type PlatformPageType = // legacy application, not under spa, and only the navigation part of the page is render using react.
	// the main content part of the page served by jira-monolith.
	// navigation part is render via src/entry/navigation-legacy
	'legacy-entry' | 'legacy-page';

// Trigger async import asap, no need for RLL as SSR renders without too
const pageContainerImport = import(
	/* webpackChunkName: "page-container-v2.nav-v3" */ '@jira/platform__page-container-v2/src/ui/legacy-page/main.tsx'
);

// eslint-disable-next-line jira/i18n/no-module-level-language-pack-import
const messages = require('language-pack');

type Params = {
	App?: ComponentType;
	containerId?: string;
	onLoad?: () => void;
	pageType?: PlatformPageType;
};

export const renderPageContainer = async ({
	// @ts-expect-error - TS2322 - Type 'null' is not assignable to type 'ComponentType<any>'.
	App = null,
	containerId = 'navigation-app',
	onLoad,
	pageType = 'legacy-page',
}: Params = {}): Promise<void> => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const container = document.getElementById(containerId);
	if (container) {
		const { default: PageContainerV2 } = await pageContainerImport;
		LooselyLazy.init({ mode: 'RENDER', crossOrigin: 'anonymous' });

		const tenantContext = getTenantContext_DEPRECATED_DO_NOT_USE();

		global.__NAV_VERSION__ = '3.0.0';

		initPlatformFeatureFlags(visualRefreshResolverOverride);
		const history = createLegacyHistory();
		const routes = getLegacyRoutes();
		const matchedRoute = matchRoute(
			routes,

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.location.pathname,

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.location.search,
			'',
		);
		setMatchedRoute(matchedRoute?.route, matchedRoute?.match);

		const resourceContext = getSpaRouterContext({ tenantContext });

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		const resourceData = window.SKELETON_NAVIGATION;

		const LegacyRouter = ({ children }: { children: ReactNode }) => (
			<Router
				history={history}
				routes={routes}
				resourceContext={resourceContext}
				resourceData={resourceData}
			>
				{children}
			</Router>
		);
		// no need for a hydration check here because legacy never has hydration, it always static fallbacks
		const pageContent = (
			<PageContainerV2
				id="jira.entry.legacy"
				locale={tenantContext.locale}
				messages={messages}
				onLoad={onLoad}
				Router={LegacyRouter}
				routerLinkComponent={JiraRouterLinkConfiguration}
				pageType={pageType}
			>
				<ErrorBoundary id="jira.route-app.legacy-bootstrap">{App ? <App /> : null}</ErrorBoundary>
			</PageContainerV2>
		);

		renderToMountPoint(<StrictMode>{pageContent}</StrictMode>, container);
	}
};

const navigationLegacy = (): void => {
	const isGlobalSidebarEnabled = getMeta('ajs-global-sidebar-enabled') === 'true';
	if (!isGlobalSidebarEnabled) return;

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	if (document.getElementById('navigation-app')) {
		renderPageContainer();
	} else {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.addEventListener('DOMContentLoaded', () => renderPageContainer());
	}
};

export default navigationLegacy;
